import { useCallback, useEffect, useRef, useState } from "react";
import { InputBase, Typography } from "@mui/material";

const EDITABLE_AUTO_COMMIT_MS = 5_000;

export function EditableValue({
  display,
  initialDraft,
  onCommit,
  minWidth = 32,
  dark = false,
}: {
  display: string;
  initialDraft: string;
  onCommit: (value: string) => void;
  minWidth?: number;
  dark?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [localDraft, setLocalDraft] = useState("");
  const draftRef = useRef("");
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const doCommit = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setEditing(false);
    onCommitRef.current(draftRef.current);
  }, []);

  const startEditing = () => {
    draftRef.current = initialDraft;
    setLocalDraft(initialDraft);
    setEditing(true);
    timerRef.current = setTimeout(doCommit, EDITABLE_AUTO_COMMIT_MS);
  };

  if (!editing) {
    return (
      <Typography
        variant="body2"
        sx={{
          minWidth,
          textAlign: "center",
          cursor: "text",
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          fontSize: "0.78rem",
          fontWeight: 600,
          color: dark ? "#fff" : "inherit",
          "&:hover": {
            bgcolor: dark ? "rgba(255,255,255,0.12)" : "action.hover",
          },
        }}
        onClick={startEditing}
      >
        {display}
      </Typography>
    );
  }

  return (
    <InputBase
      autoFocus
      value={localDraft}
      onChange={(e) => {
        draftRef.current = e.target.value;
        setLocalDraft(e.target.value);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(doCommit, EDITABLE_AUTO_COMMIT_MS);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") doCommit();
        if (e.key === "Escape") {
          if (timerRef.current) clearTimeout(timerRef.current);
          setEditing(false);
        }
      }}
      onBlur={doCommit}
      inputProps={{
        style: {
          textAlign: "center",
          fontSize: "0.78rem",
          padding: "2px",
          color: dark ? "#fff" : undefined,
        },
      }}
      sx={{
        width: minWidth + 8,
        border: "1px solid",
        borderColor: dark ? "rgba(255,255,255,0.25)" : "divider",
        borderRadius: 1,
        bgcolor: dark ? "rgba(255,255,255,0.1)" : "background.paper",
      }}
    />
  );
}
